import { TxInterface } from 'interfaces/tx.interface'
import { useMemo, JSX, Dispatch, SetStateAction, useState } from 'react'
import { CopyTooltip } from 'components/tooltip/copy-tooltip'
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa'
import { BlockNumber } from 'components/common/BlockNumber'
import Moment from 'react-moment'
import { toBalanceString } from 'utils'
import { sentenceCase } from 'text-case'
import { BBABalance } from 'components/blockchain'
import InputField from 'components/fields/InputField'
import { MdEdit } from 'react-icons/md'
import { Link } from 'react-router-dom'
import { PiQuestionLight } from 'react-icons/pi'
import ReactTooltip from 'react-tooltip'

type OverviewDataContent = {
	key: string
	content: JSX.Element
	info: string
	onlyShowOn?: 'vote' | 'system'
}

function WithCopyTooltip({ data }: { data: string }) {
	return (
		<div className="flex space-x-2 items-center">
			<p className="text-small truncate">{data}</p>
			<CopyTooltip secretValue={data} iconSize="sm" />
		</div>
	)
}

function CreateUserLabelModal({ setCardOpen }: { setCardOpen: Dispatch<SetStateAction<boolean>> }) {
	return (
		<div className="modal-overlay" onClick={() => setCardOpen(false)}>
			<div className="modal py-2" onClick={(e) => e.stopPropagation()}>
				<div className="modal-header">
					<p className="text-medium px-5 py-2 !font-semibold">Create User Label</p>
				</div>
				<div className="modal-body p-5">
					<InputField
						variant="auth"
						extra="mb-5"
						label="Label"
						placeholder="Label"
						id="user_label"
						type="text"
						required={true}
					/>
					<div className="mb-6">
						<label className="text-small ml-1.5 font-medium text-gray-800 dark:text-gray-300">Notes</label>
						<textarea
							placeholder="Notes"
							rows={6}
							className={`mt-1 flex w-full items-center justify-center rounded-[6px] border border-gray-200 bg-white py-1 pl-3 pr-10 text-small outline-none dark:!border-white/10 dark:bg-gray-900 dark:text-white`}
						></textarea>
					</div>
				</div>
				<div className="mt-2 flex gap-2 px-5 py-1">
					<button className="btn-outline ml-auto" onClick={() => setCardOpen(false)}>
						Cancel
					</button>
					<button className="btn">Save</button>
				</div>
			</div>
		</div>
	)
}

export default function OverviewDetailCard({ data }: { data: TxInterface }) {
	const { meta, transaction, signature, slot, info, version } = data

	const [modalOpen, setModalOpen] = useState<boolean>(false)

	const isError = meta.err
	const isSystem = transaction.message.instructions[0].program === 'system'

	const timeStamps = info.timestamp
	const currentBlockHash = transaction.message.recentBlockhash

	const bbaTransfered = isSystem && toBalanceString(transaction.message.instructions[0].parsed.info.daltons, 9)
	const bbaDestination = isSystem && transaction.message.instructions[0].parsed.info.destination
	const bbaSource = isSystem && transaction.message.instructions[0].parsed.info.source

	const OverviewData = useMemo<OverviewDataContent[]>(
		() => [
			{
				key: 'Transaction Hash',
				content: <WithCopyTooltip data={signature} />,
				info: 'A Transaction Hash, often referred to as TxHash, is a distinct identifier consisting of 66 characters that is created each time a transaction takes place.'
			},
			{
				key: 'Status',
				content: (
					<div className={`flex space-x-1.5 items-center badge badge-${isError ? 'error' : 'success'}`}>
						{isError ? <FaTimesCircle className="text-xs" /> : <FaCheckCircle className="text-xs" />}
						<p>{isError ? 'Failure' : 'Success'}</p>
					</div>
				),
				info: 'The status of the Transaction.'
			},
			{
				key: 'Block',
				content: <BlockNumber block={slot} link />,
				info: 'The block number where the transaction is logged. Block confirmations show the count of blocks that have been added after the transaction was created.'
			},
			{
				key: 'Timestamp',
				content: <Moment className="!text-small" date={timeStamps * 1000} fromNow />,
				info: 'The moment when a transaction occurs.'
			},
			{
				key: 'Transaction Action',
				content: (
					<p>
						{' '}
						<span className="mr-2">Transfer {`${bbaTransfered} BBA`}</span>
						<span className="mr-2 text-green-900">{bbaSource}</span>
						<span className="mr-2 text-gray-700">To</span>
						<span className="mr-1 text-green-900">{bbaDestination}</span>
						<span className="badge badge-info">{sentenceCase(version)}</span>
					</p>
				),
				info: 'Overview of the actions taken during the transaction.',
				onlyShowOn: 'system'
			},
			{
				key: 'From',
				content: <WithCopyTooltip data={bbaSource} />,
				info: 'The party initiating the transaction.',
				onlyShowOn: 'system'
			},
			{
				key: 'To',
				content: <WithCopyTooltip data={bbaDestination} />,
				info: 'The party receiving the transaction, which may be a contract address.',
				onlyShowOn: 'system'
			},
			{
				key: 'Previous Block Hash',
				content: <WithCopyTooltip data={currentBlockHash} />,
				info: 'Total fee charged for processing the transaction in BBA and its equivalent fiat value.'
			},
			{
				key: 'Transaction Fee',
				content: <BBABalance daltons={meta.fee} displayFiat />,
				info: 'Cost per unit of gas spent for the transaction, in BBA'
			},
			{
				key: 'Your Note',
				content: (
					<button className="flex items-center text-green-900" onClick={() => setModalOpen(true)}>
						Add Note{' '}
						<span className="text-small ml-1">
							<MdEdit />
						</span>{' '}
					</button>
				),
				info: 'Reference to the last proof of history hash in the slot.'
			}
		],
		[
			bbaDestination,
			bbaSource,
			bbaTransfered,
			currentBlockHash,
			isError,
			meta.fee,
			signature,
			slot,
			timeStamps,
			version
		]
	)

	return (
		<div className="box mb-4 overflow-hidden bg-white px-2.5 py-5 dark:bg-darkPrimary md:px-5">
			{modalOpen && <CreateUserLabelModal setCardOpen={setModalOpen} />}
			<ReactTooltip id="question-tip" place="right" effect="solid" class="custom-tooltip right" />
			<h1 className="text-large border-b-[1px] border-gray-100 py-4 dark:border-lightGrey">Overview</h1>
			<div className="mt-3 flex flex-col">
				{OverviewData.filter((data) => !data.onlyShowOn || (data.onlyShowOn === 'system' && isSystem)).map((data) => (
					<div className="grid grid-cols-2 gap-3 py-3 md:grid-cols-12" key={data.key}>
						<div className="flex items-center md:col-span-3">
							<Link
								to="#"
								className="text-medium mr-2 text-gray-700 dark:text-gray-300"
								data-for="question-tip"
								data-tip={data.info}
							>
								<PiQuestionLight />
							</Link>
							<p className="text-small text-gray-700 dark:text-gray-300">{data.key}:</p>
						</div>
						<div className="md:col-span-9">{data.content}</div>
					</div>
				))}
			</div>
		</div>
	)
}
